/**
 * حالت مشاوره‌ی جاری و جهش‌های خط لوله.
 *
 * ⚠️ فقط `consultation_id` سمت کلاینت نگه داشته می‌شود؛ خودِ حالت سمت سرور
 * است. اگر کل حالت به کلاینت می‌آمد و بازگردانده می‌شد، کاربر می‌توانست شواهد
 * بازیابی‌شده و امتیازها را دستکاری کند و مشاور روی شواهد ساختگی استدلال
 * می‌کرد.
 *
 * ترتیب جهش‌ها همان ترتیب سرور است و اینجا بازچینش نمی‌شود.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/lib/api/client'
import {
  closeConsultation,
  refineConsultation,
  startConsultation,
  submitAnswers,
  submitFeedback,
  type Scope,
} from '@/lib/api/endpoints'
import { queryKeys } from '@/lib/queries'

import type { ConsultationState, UserFeedback } from '@/lib/types'

/**
 * واکنش به پایان یک تماس.
 *
 * وجودش برای رابط گفت‌وگوست: آن رابط باید در **لحظه‌ی رسیدن** پاسخ یک پیام تازه
 * به رشته اضافه کند. جایگزینش تماشای تغییر `state` با `useEffect` بود که هم یک
 * رندر اضافه می‌ساخت و هم تشخیص «این پاسخ تازه است یا همان قبلی» را به مقایسه‌ی
 * شکننده وابسته می‌کرد.
 */
interface CallOptions {
  onDone?: (state: ConsultationState) => void
}

export interface UseConsultation {
  state: ConsultationState | null
  /** نمایه‌ی مرحله برای نشانگر: پرونده، شفاف‌سازی، نتیجه. */
  step: number
  isBusy: boolean
  error: ApiError | null
  start: (caseText: string, options?: CallOptions) => void
  answer: (answers: (string | null)[], options?: CallOptions) => void
  refine: (
    payload: { revisedText: string; note: string; answers: (string | null)[] },
    options?: CallOptions,
  ) => void
  giveFeedback: (feedback: UserFeedback, correction: string) => void
  /** حالت آماده را می‌پذیرد؛ برای ادامه‌دادن یک مشاوره‌ی بایگانی‌شده. */
  adopt: (state: ConsultationState) => void
  reset: () => void
}

/** `onDone` را به شکل مورد انتظار `mutate` درمی‌آورد. */
const callbacks = (
  options?: CallOptions,
): { onSuccess: (state: ConsultationState) => void } | undefined =>
  options?.onDone === undefined ? undefined : { onSuccess: options.onDone }

const stepOf = (state: ConsultationState | null): number => {
  if (state === null) {
    return 0
  }
  if (state.stage === 'awaiting_clarification') {
    return 1
  }
  return 2
}

export const useConsultation = (scope?: Scope): UseConsultation => {
  const [state, setState] = useState<ConsultationState | null>(null)
  const queryClient = useQueryClient()

  /**
   * پس از هر مشاوره‌ی کامل، آرشیو بی‌اعتبار می‌شود.
   *
   * لازم است چون سرویس در پایان `complete` یک رکورد در لاگ می‌نویسد؛ بدون
   * بی‌اعتبارسازی، کاربر به آرشیو می‌رفت و مشاوره‌ی همین لحظه را نمی‌دید.
   */
  const invalidateArchive = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.archivePrefix })
  }, [queryClient])

  const startMutation = useMutation({
    mutationFn: (caseText: string) => startConsultation(caseText, scope),
    onSuccess: (next) => {
      setState(next)
      invalidateArchive()
    },
  })

  const answerMutation = useMutation({
    mutationFn: (answers: (string | null)[]) => {
      if (state === null) {
        throw new ApiError({ message: 'مشاوره‌ای در جریان نیست.', status: 0 })
      }
      return submitAnswers(state.consultation_id, answers)
    },
    onSuccess: (next) => {
      setState(next)
      invalidateArchive()
    },
  })

  const refineMutation = useMutation({
    mutationFn: (payload: {
      revisedText: string
      note: string
      answers: (string | null)[]
    }) => {
      if (state === null) {
        throw new ApiError({ message: 'مشاوره‌ای در جریان نیست.', status: 0 })
      }
      return refineConsultation(state.consultation_id, {
        revised_text: payload.revisedText,
        note: payload.note,
        answers: payload.answers,
      })
    },
    onSuccess: (next) => {
      setState(next)
      invalidateArchive()
      toast.success('برآورد تازه ساخته شد.')
    },
  })

  const feedbackMutation = useMutation({
    mutationFn: ({
      feedback,
      correction,
    }: {
      feedback: UserFeedback
      correction: string
    }) => {
      if (state === null) {
        throw new ApiError({ message: 'مشاوره‌ای در جریان نیست.', status: 0 })
      }
      return submitFeedback(state.consultation_id, feedback, correction)
    },
    onSuccess: (result) => {
      invalidateArchive()
      toast.success(result.message)
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  /**
   * حالتی که از بیرون آمده را می‌پذیرد، بدون تماس تازه.
   *
   * مسیر «ادامه‌دادن از آرشیو» از این استفاده می‌کند: سرور مشاوره را از لاگ به
   * حافظه برگردانده و همان `consultation_id` باید مبنای `refine` بعدی شود.
   */
  const adopt = useCallback((next: ConsultationState) => {
    setState(next)
  }, [])

  const reset = useCallback(() => {
    const current = state
    setState(null)
    // بستن پرونده در سرور، حالت را از حافظه پاک می‌کند. رکورد لاگ دست‌نخورده
    // می‌ماند، پس آرشیو چیزی از دست نمی‌دهد.
    if (current !== null) {
      void closeConsultation(current.consultation_id).catch(() => {
        // بستن یک راحتی است، نه الزام: حالت خودش با TTL منقضی می‌شود.
      })
    }
  }, [state])

  const firstError = [startMutation.error, answerMutation.error, refineMutation.error].find(
    (item): item is ApiError => item instanceof ApiError,
  )

  return {
    state,
    step: stepOf(state),
    isBusy: startMutation.isPending || answerMutation.isPending || refineMutation.isPending,
    error: firstError ?? null,
    // `mutate` خودش callback به‌ازای هر تماس می‌پذیرد؛ پس onDone بدون حالت
    // اضافه و بدون effect کار می‌کند.
    start: (caseText: string, options?: CallOptions) => {
      startMutation.mutate(caseText, callbacks(options))
    },
    answer: (answers: (string | null)[], options?: CallOptions) => {
      answerMutation.mutate(answers, callbacks(options))
    },
    refine: (payload, options?: CallOptions) => {
      refineMutation.mutate(payload, callbacks(options))
    },
    giveFeedback: (feedback: UserFeedback, correction: string) => {
      feedbackMutation.mutate({ feedback, correction })
    },
    adopt,
    reset,
  }
}
